import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
Alert,
Avatar,
Box,
Button,
Card,
CardContent,
CardHeader,
CircularProgress,
Container,
Divider,
Grid,
IconButton,
InputAdornment,
MenuItem,
Modal,
Paper,
Select,
Tab,
Tabs,
TextField,
Typography,
styled,
useMediaQuery,
useTheme,
FormControl,
InputLabel
} from '@mui/material';
import {
Facebook,
Instagram,
LinkedIn,
Twitter,
Visibility,
VisibilityOff,
Close,
Delete,
Save,
Edit,
Lock,
Person,
PhotoCamera,
School,
Phone,
Email,
Flag,
Info,
Badge as BadgeIcon
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import {
    APIResearcherDelete,
    APIUniversitiesList,
    APIUpdateResearcherDetails,
    APIUpdateResearcherImage, 
    APIUpdateResearcherPassword} from '../../utilities/APIS';
import { clearLocalStorageData, getIdCategory } from '../../utilities/Functions';
import GuestUser from '../main/GuestUser';
import NavigationMenu from './NavigationMenu';
import { BASE_URL } from '../../utilities/Env';
import useCustomToast from '../../hooks/useToast';
import { TEXT_CONNECTION_ERROR } from '../../utilities/Text';
import { headers } from '../../utilities/Env';
import LoadingState from '../../components/LoadingState';

const StyledTabs = styled(Tabs)(({ theme }) => ({
    '& .MuiTabs-indicator': {
        backgroundColor: theme.palette.primary.main,
    },
}));

const StyledTab = styled(Tab)(({ theme }) => ({
    textTransform: 'none',
    fontWeight: theme.typography.fontWeightRegular,
    fontSize: theme.typography.pxToRem(15),
    marginRight: theme.spacing(1),
    color: theme.palette.text.secondary,
    '&.Mui-selected': {
        color: theme.palette.primary.main,
    },
}));

const ProfileImageUpload = styled('div')({
    position: 'relative',
    width: 150,
    height: 150,
    margin: '0 auto',
    '&:hover': {
        '& .MuiIconButton-root': {
            opacity: 1,
        }
    }
});

const ProfileImage = styled(Avatar)({
    width: '100%',
    height: '100%',
    cursor: 'pointer',
});

const UploadButton = styled(IconButton)({
    position: 'absolute',
    bottom: 0,
    right: 0,
    backgroundColor: 'rgba(0,0,0,0.5)',
    color: 'white',
    opacity: 0,
    transition: 'opacity 0.3s',
    '&:hover': {
        backgroundColor: 'rgba(0,0,0,0.7)',
    }
});

const Profile = () => {
    const navigate = useNavigate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const [error, setError] = useState(null);
    const [userLoggedIn, setUserLoggedIn] = useState(false);
    const showToast = useCustomToast();
    const [activeTab, setActiveTab] = useState('overview');
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const [imageUploading, setImageUploading] = useState(false);
    const [imageError, setImageError] = useState(false);

    // User data state
    const [userData, setUserData] = useState({
        id: '',
        name: '',
        email: '',
        title: '',
        contact: '',
        university: '',
        nationality: '',
        about: '',
        level: '',
        twitter: '',
        facebook: '',
        instagram: '',
        linkedin: '',
        profileImage: ''
    });

    // Form states
    const [passwordData, setPasswordData] = useState({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
    });

    const [imageData, setImageData] = useState({
        selectedImage: null,
        uploadImage: null,
        fileNameToDelete: ''
    });

    const [universities, setUniversities] = useState([]);

    // Animation variants
    const fadeIn = {
        hidden: { opacity: 0 },
        visible: { opacity: 1, transition: { duration: 0.5 } }
    };

    useEffect(() => {
        loadUser();
        fetchData();
    }, []);

    const fetchData = async () => {
        setLoading(true);
        setError(null);

        try {
            const [unisRes] = await Promise.all([
                axios.get(APIUniversitiesList, { headers }),
            ]);

            const data = Array.isArray(unisRes.data.data) ? unisRes.data.data.reverse() : [];
            setUniversities(data);
        } catch (error) {
            let errorMessage = "Failed to load data";
            if (error.response) {
                errorMessage = error.response.data.message || error.message;
            }
            else if (error.code === "ERR_NETWORK") {
                errorMessage = TEXT_CONNECTION_ERROR;
            }
            setError(errorMessage);
            console.log(errorMessage);
            showToast("Error", errorMessage, "error");
        } finally {
            setLoading(false);
        }
    };

    // Helper function to get full image URL
    const getFullImageUrl = (imagePath) => {
        if (!imagePath || imagePath === 'pendding' || imagePath === 'pending' || imagePath === 'null') {
            return null;
        }
        if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
            return imagePath;
        }
        if (imagePath.startsWith('/images/')) {
            return `${BASE_URL}${imagePath}`;
        }
        return `${BASE_URL}/images/researcher/${imagePath}`;
    };

    const loadUser = () => {
        // Load researcher data from session storage
        const user = sessionStorage.getItem('reseacher');
        // Load profile image from separate storage
        const profileImg = sessionStorage.getItem('profile');
        
        console.log('Loading user data...');
        console.log('Profile image from storage:', profileImg);
        
        if (user) {
            const jsonData = JSON.parse(user);
            setUserData({
                id: jsonData.id,
                name: jsonData.name || '',
                email: jsonData.email || '',
                title: jsonData.title || '',
                contact: jsonData.contact || '',
                university: jsonData.university || '',
                nationality: jsonData.nationality || '',
                about: jsonData.about === 'pendding' ? '' : (jsonData.about || ''),
                level: jsonData.level === 'pendding' ? '' : (jsonData.level || ''),
                twitter: jsonData.twitter === 'pendding' ? '' : (jsonData.twitter || ''),
                facebook: jsonData.facebook === 'pendding' ? '' : (jsonData.facebook || ''),
                instagram: jsonData.instagram === 'pendding' ? '' : (jsonData.instagram || ''),
                linkedin: jsonData.linkedin === 'pendding' ? '' : (jsonData.linkedin || ''),
                profileImage: profileImg || ''
            });
            setUserLoggedIn(jsonData.loggedIn || false);
            setImageError(false);
        } else {
            console.log('No user data found in session storage');
        }
    };

    // Update session storage with new user data (excluding profile image)
    const updateSessionStorage = (updatedData) => {
        const user = sessionStorage.getItem('reseacher');
        if (user) {
            const jsonData = JSON.parse(user);
            const updatedUser = { ...jsonData, ...updatedData };
            sessionStorage.setItem('reseacher', JSON.stringify(updatedUser));
            setUserData(prev => ({ ...prev, ...updatedData }));
        }
    };

    // Update profile image in separate storage
    const updateProfileImage = (imagePath) => {
        sessionStorage.setItem('profile', imagePath);
        setUserData(prev => ({ ...prev, profileImage: imagePath }));
        // Also trigger a storage event for other tabs/components
        window.dispatchEvent(new Event('storage'));
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setUserData(prev => ({ ...prev, [name]: value }));
    };

    const handlePasswordChange = (e) => {
        const { name, value } = e.target;
        setPasswordData(prev => ({ ...prev, [name]: value }));
    };

    const handleImageUpload = (e) => {
        const file = e.target.files[0];
        if (file) {
            // Validate file type
            const allowedTypes = ['image/jpeg', 'image/png', 'image/jpg', 'image/gif'];
            if (!allowedTypes.includes(file.type)) {
                showToast('Error', 'Please upload a valid image file (JPEG, PNG, GIF)', 'error');
                return;
            }
            
            // Validate file size (max 2MB)
            if (file.size > 2 * 1024 * 1024) {
                showToast('Error', 'Image size should be less than 2MB', 'error');
                return;
            }

            const reader = new FileReader();
            reader.onloadend = () => {
                setImageData(prev => ({
                    ...prev,
                    selectedImage: reader.result,
                    uploadImage: file
                }));
            };
            reader.readAsDataURL(file);
        }
    };

    const validateEmail = (email) => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    };

    const handleSubmit = async (actionStatus) => {
        const { id, university, nationality, email, name, contact, about, level, twitter, facebook, instagram, linkedin, title } = userData;
        const { newPassword, confirmPassword } = passwordData;

        if (actionStatus === "IMAGE") {
            if (!imageData.uploadImage) {
                showToast('Error', 'Please select an image to upload', 'error');
                return;
            }
        }

        if (actionStatus === "PROFILE") {
            if (!name || !contact || !email) {
                showToast('Error', 'Name, Contact and Email are required', 'error');
                return;
            }
            if (!validateEmail(email)) {
                showToast('Error', 'Please enter a valid email address', 'error');
                return;
            }
        } else if (actionStatus === "PASSWORD") {
            if (!newPassword || !confirmPassword) {
                showToast('Error', 'All password fields are required', 'error');
                return;
            }
            if (newPassword !== confirmPassword) {
                showToast('Error', 'Passwords do not match', 'error');
                return;
            }
            if (newPassword.length < 8) {
                showToast('Error', 'Password must be at least 8 characters', 'error');
                return;
            }
        }

        const formDataToSend = new FormData();
        formDataToSend.append('university', university);
        formDataToSend.append('nationality', nationality);
        formDataToSend.append('email', email);
        formDataToSend.append('fullName', name);
        formDataToSend.append('contact', contact);
        formDataToSend.append('about', about);
        formDataToSend.append('level', level);
        formDataToSend.append('twitter', twitter);
        formDataToSend.append('facebook', facebook);
        formDataToSend.append('title', title);
        formDataToSend.append('instagram', instagram);
        formDataToSend.append('linkedin', linkedin);
        formDataToSend.append('password', newPassword);
        if (imageData.uploadImage) {
            formDataToSend.append('image', imageData.uploadImage);
        }
        formDataToSend.append('updateId', id);

        try {
            let postRequest;
            if (actionStatus === "PROFILE") {
                showToast('Info', 'Updating profile...', 'info');
                postRequest = await axios.post(APIUpdateResearcherDetails, formDataToSend, { headers });
            } else if (actionStatus === "PASSWORD") {
                showToast('Info', 'Updating password...', 'info');
                postRequest = await axios.post(APIUpdateResearcherPassword, formDataToSend, { headers });
            } else if (actionStatus === "IMAGE") {
                setImageUploading(true);
                showToast('Info', 'Uploading image...', 'info');
                postRequest = await axios.post(APIUpdateResearcherImage, formDataToSend, { 
                    headers: {
                        ...headers,
                        'Content-Type': 'multipart/form-data'
                    }
                });
            } else if (actionStatus === "DELETE") {
                postRequest = await axios.post(APIResearcherDelete, formDataToSend, { headers });
            }

            const response = postRequest.data;
            if (response.status === "success") {
                if (actionStatus === "DELETE") {
                    clearLocalStorageData();
                    navigate('/');
                    showToast("Success", response.message, "success");
                    return;
                }
                
                // Update session storage with new data
                if (actionStatus === "PROFILE") {
                    updateSessionStorage({
                        name: name,
                        email: email,
                        title: title,
                        contact: contact,
                        university: university,
                        nationality: nationality,
                        about: about,
                        level: level,
                        twitter: twitter,
                        facebook: facebook,
                        instagram: instagram,
                        linkedin: linkedin
                    });
                    showToast("Success", "Profile updated successfully!", "success");
                    setTimeout(() => {
                        loadUser();
                    }, 1000);
                } else if (actionStatus === "PASSWORD") {
                    showToast("Success", "Password updated successfully! Please log in again.", "success");
                    setTimeout(() => {
                        clearLocalStorageData();
                        navigate('/');
                    }, 3000);
                } else if (actionStatus === "IMAGE") {
                    // Update profile image in separate storage
                    const newImagePath = response.data?.profile_image;
                    if (newImagePath) {
                        updateProfileImage(newImagePath);
                        showToast("Success", "Profile image updated successfully!", "success");
                        
                        // Clear image selection
                        setImageData(prev => ({
                            ...prev,
                            selectedImage: null,
                            uploadImage: null
                        }));
                        
                        // Force reload user data to reflect new image
                        setTimeout(() => {
                            loadUser();
                            setActiveTab('overview');
                            // Force re-render of navigation menu
                            window.dispatchEvent(new Event('storage'));
                        }, 500);
                    } else {
                        showToast("Success", "Profile image updated successfully!", "success");
                        loadUser();
                    }
                }
            } else {
                showToast("Error", response.message || "Unknown Server Error, Data Not Posted", "error");
            }
        } catch (error) {
            console.error('Error in handleSubmit:', error);
            if (error.response) {
                if (error.response.status === 422) {
                    const errors = error.response.data.errors;
                    let errorMessages = [];
                    for (const field in errors) {
                        errorMessages.push(...errors[field]);
                    }
                    showToast("Validation Error", errorMessages.join("\n"), "error");
                } else {
                    showToast("Error", error.response.data.message || "An error occurred", "error");
                }
            } else if (error.code === "ERR_NETWORK") {
                showToast("Connection Error", TEXT_CONNECTION_ERROR, "error");
            } else {
                showToast("Error", "An unexpected error occurred", "error");
            }
        } finally {
            setImageUploading(false);
        }
    };

    const handleTabChange = (event, newValue) => {
        setActiveTab(newValue);
    };

    const renderProfileOverview = () => (
        <motion.div variants={fadeIn} initial="hidden" animate="visible">
            <Typography variant="h6" gutterBottom>
                Professional brief
            </Typography>
            <Typography variant="body1" paragraph sx={{ fontStyle: 'italic', mb: 3 }}>
                {userData.about || 'No information provided'}
            </Typography>

            <Typography variant="h6" gutterBottom>
                Profile Details
            </Typography>

            <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={12} sm={4}>
                    <Typography variant="subtitle2">Full Name</Typography>
                </Grid>
                <Grid item xs={12} sm={8}>
                    <Typography>{userData.name}</Typography>
                </Grid>
            </Grid>

            <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={12} sm={4}>
                    <Typography variant="subtitle2">University</Typography>
                </Grid>
                <Grid item xs={12} sm={8}>
                    <Typography>{getIdCategory(userData.university, universities)}</Typography>
                </Grid>
            </Grid>

            <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={12} sm={4}>
                    <Typography variant="subtitle2">Level</Typography>
                </Grid>
                <Grid item xs={12} sm={8}>
                    <Typography>{userData.level || 'Not specified'}</Typography>
                </Grid>
            </Grid>

            <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={12} sm={4}>
                    <Typography variant="subtitle2">Country</Typography>
                </Grid>
                <Grid item xs={12} sm={8}>
                    <Typography>{userData.nationality}</Typography>
                </Grid>
            </Grid>

            <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={12} sm={4}>
                    <Typography variant="subtitle2">Phone</Typography>
                </Grid>
                <Grid item xs={12} sm={8}>
                    <Typography>{userData.contact}</Typography>
                </Grid>
            </Grid>

            <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={4}>
                    <Typography variant="subtitle2">Email</Typography>
                </Grid>
                <Grid item xs={12} sm={8}>
                    <Typography>{userData.email}</Typography>
                </Grid>
            </Grid>

            <Button
                variant="contained"
                color="error"
                fullWidth
                startIcon={<Delete />}
                onClick={() => setShowDeleteModal(true)}
                sx={{ mb: 2 }}
            >
                Delete My Account
            </Button>
        </motion.div>
    );

    const renderEditProfile = () => (
        <motion.div variants={fadeIn} initial="hidden" animate="visible">
            <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                    <TextField
                        fullWidth
                        label="Full Name"
                        name="name"
                        value={userData.name}
                        onChange={handleInputChange}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <BadgeIcon />
                                </InputAdornment>
                            ),
                        }}
                    />
                </Grid>
                
                <Grid item xs={12} md={6}>
                    <TextField
                        fullWidth
                        label="Title"
                        name="title"
                        value={userData.title}
                        onChange={handleInputChange}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <Person />
                                </InputAdornment>
                            ),
                        }}
                    />
                </Grid>
                
                <Grid item xs={12}>
                    <TextField
                        fullWidth
                        multiline
                        rows={3}
                        label="Professional brief"
                        name="about"
                        value={userData.about}
                        onChange={handleInputChange}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <Info />
                                </InputAdornment>
                            ),
                        }}
                    />
                </Grid>
                
                <Grid item xs={12} md={6}>
                    <FormControl fullWidth>
                        <InputLabel>University</InputLabel>
                        <Select
                            name="university"
                            value={userData.university}
                            onChange={handleInputChange}
                            label="University"
                        >
                            <MenuItem value="">Select University</MenuItem>
                            {universities.map((univ, index) => (
                                <MenuItem key={index} value={univ.id}>{univ.name}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Grid>
                
                <Grid item xs={12} md={6}>
                    <FormControl fullWidth>
                        <InputLabel>Education Level</InputLabel>
                        <Select
                            name="level"
                            value={userData.level}
                            onChange={handleInputChange}
                            label="Education Level"
                        >
                            <MenuItem value="">Select Level</MenuItem>
                            <MenuItem value="Degree">Degree</MenuItem>
                            <MenuItem value="Masters">Masters</MenuItem>
                            <MenuItem value="PHD">PHD</MenuItem>
                            <MenuItem value="Professor">Professor</MenuItem>
                            <MenuItem value="Researcher">Researcher</MenuItem>
                        </Select>
                    </FormControl>
                </Grid>
                
                <Grid item xs={12} md={6}>
                    <TextField
                        fullWidth
                        label="Nationality"
                        name="nationality"
                        value={userData.nationality}
                        onChange={handleInputChange}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <Flag />
                                </InputAdornment>
                            ),
                        }}
                    />
                </Grid>
                
                <Grid item xs={12} md={6}>
                    <TextField
                        fullWidth
                        label="Phone"
                        name="contact"
                        value={userData.contact}
                        onChange={handleInputChange}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <Phone />
                                </InputAdornment>
                            ),
                        }}
                    />
                </Grid>
                
                <Grid item xs={12}>
                    <TextField
                        fullWidth
                        label="Email"
                        name="email"
                        type="email"
                        value={userData.email}
                        onChange={handleInputChange}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <Email />
                                </InputAdornment>
                            ),
                        }}
                    />
                </Grid>
                
                <Grid item xs={12} md={6}>
                    <TextField
                        fullWidth
                        label="Twitter Profile"
                        name="twitter"
                        value={userData.twitter}
                        onChange={handleInputChange}
                        placeholder="https://twitter.com/#"
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <Twitter />
                                </InputAdornment>
                            ),
                        }}
                    />
                </Grid>
                
                <Grid item xs={12} md={6}>
                    <TextField
                        fullWidth
                        label="Facebook Profile"
                        name="facebook"
                        value={userData.facebook}
                        onChange={handleInputChange}
                        placeholder="https://facebook.com/#"
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <Facebook />
                                </InputAdornment>
                            ),
                        }}
                    />
                </Grid>
                
                <Grid item xs={12} md={6}>
                    <TextField
                        fullWidth
                        label="Instagram Profile"
                        name="instagram"
                        value={userData.instagram}
                        onChange={handleInputChange}
                        placeholder="https://instagram.com/#"
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <Instagram />
                                </InputAdornment>
                            ),
                        }}
                    />
                </Grid>
                
                <Grid item xs={12} md={6}>
                    <TextField
                        fullWidth
                        label="LinkedIn Profile"
                        name="linkedin"
                        value={userData.linkedin}
                        onChange={handleInputChange}
                        placeholder="https://linkedin.com/#"
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <LinkedIn />
                                </InputAdornment>
                            ),
                        }}
                    />
                </Grid>
                <Grid item xs={12}>
                    <Button
                        variant="contained"
                        startIcon={<Save />}
                        onClick={() => handleSubmit("PROFILE")}
                        fullWidth={isMobile}
                        sx={{ mt: 2 }}
                        disabled={loading}
                    >
                        Save Changes
                    </Button>
                </Grid>
            </Grid>
        </motion.div>
    );

    const renderChangePassword = () => (
        <motion.div variants={fadeIn} initial="hidden" animate="visible">
            <Grid container spacing={3}>
                <Grid item xs={12}>
                    <TextField
                        fullWidth
                        label="New Password"
                        name="newPassword"
                        type={showPassword ? 'text' : 'password'}
                        value={passwordData.newPassword}
                        onChange={handlePasswordChange}
                        helperText="Password must be at least 8 characters with uppercase, lowercase, number and special character"
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <Lock />
                                </InputAdornment>
                            ),
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton
                                        onClick={() => setShowPassword(!showPassword)}
                                        edge="end"
                                    >
                                        {showPassword ? <VisibilityOff /> : <Visibility />}
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                    />
                </Grid>
                
                <Grid item xs={12}>
                    <TextField
                        fullWidth
                        label="Confirm New Password"
                        name="confirmPassword"
                        type={showPassword ? 'text' : 'password'}
                        value={passwordData.confirmPassword}
                        onChange={handlePasswordChange}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <Lock />
                                </InputAdornment>
                            ),
                        }}
                    />
                </Grid>

                <Grid item xs={12}>
                    <Button
                        variant="contained"
                        startIcon={<Save />}
                        onClick={() => handleSubmit("PASSWORD")}
                        fullWidth={isMobile}
                        sx={{ mt: 2 }}
                        disabled={loading}
                    >
                        Change Password
                    </Button>
                </Grid>
            </Grid>
        </motion.div>
    );

    const renderImageUpload = () => (
        <motion.div variants={fadeIn} initial="hidden" animate="visible">
            <Box display="flex" flexDirection="column" alignItems="center" mb={3}>
                <ProfileImageUpload>
                    <ProfileImage
                        src={imageData.selectedImage || getFullImageUrl(userData.profileImage) || ''}
                        alt="Profile"
                        onError={() => setImageError(true)}
                    />
                    <input
                        accept="image/*"
                        style={{ display: 'none' }}
                        id="profile-image-upload"
                        type="file"
                        onChange={handleImageUpload}
                    />
                    <label htmlFor="profile-image-upload">
                        <UploadButton component="span">
                            <PhotoCamera />
                        </UploadButton>
                    </label>
                </ProfileImageUpload>
                <Typography variant="caption" color="textSecondary" mt={1}>
                    {imageData.selectedImage ? 'New image selected - Click save to update' : 'Click camera icon to change profile image'}
                </Typography>
                <Typography variant="caption" color="primary" mt={0.5}>
                    Recommended: Square image, max 2MB (JPG, PNG)
                </Typography>
            </Box>
            
            <Button
                variant="contained"
                color="primary"
                startIcon={imageUploading ? <CircularProgress size={20} /> : <Save />}
                onClick={() => handleSubmit("IMAGE")}
                fullWidth={isMobile}
                disabled={!imageData.selectedImage || imageUploading}
                sx={{ py: 1.5 }}
            >
                {imageUploading ? 'Uploading...' : 'Save New Image'}
            </Button>
        </motion.div>
    );

    if (error) return showToast("Error", error, "error");

    if (loading) {
        return (
            <>
                <NavigationMenu />
                <LoadingState />
            </>
        );
    }

    return (
        <div style={{ backgroundColor: theme.palette.background.default, minHeight: '100vh' }}>
            <NavigationMenu />

            <Container maxWidth="lg" sx={{ py: 4, marginTop: 5 }}>
                {userLoggedIn ? (
                    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.5 }}>
                        <Paper elevation={3} sx={{ mb: 4 }}>
                            <Card>
                                <CardHeader
                                    title={
                                        <Typography variant="h4" component="div">
                                            User Profile
                                        </Typography>
                                    }
                                    subheader="Manage your account information"
                                />

                                <CardContent>
                                    <Grid container spacing={4}>
                                        <Grid item xs={12} md={4}>
                                            <Box display="flex" flexDirection="column" alignItems="center">
                                                <Avatar
                                                    src={getFullImageUrl(userData.profileImage) || ''}
                                                    sx={{ width: 150, height: 150, mb: 2 }}
                                                    onError={() => setImageError(true)}
                                                >
                                                    <Person sx={{ fontSize: 80 }} />
                                                </Avatar>
                                                <Typography variant="h5" gutterBottom>
                                                    {userData.title} {userData.name}
                                                </Typography>
                                                <Typography variant="subtitle1" color="textSecondary" gutterBottom>
                                                    {userData.level || 'Researcher'}
                                                </Typography>

                                                <Box display="flex" mt={2}>
                                                    {userData.twitter && userData.twitter !== 'pendding' && (
                                                        <IconButton href={userData.twitter} target="_blank">
                                                            <Twitter color="primary" />
                                                        </IconButton>
                                                    )}
                                                    {userData.facebook && userData.facebook !== 'pendding' && (
                                                        <IconButton href={userData.facebook} target="_blank">
                                                            <Facebook color="primary" />
                                                        </IconButton>
                                                    )}
                                                    {userData.instagram && userData.instagram !== 'pendding' && (
                                                        <IconButton href={userData.instagram} target="_blank">
                                                            <Instagram color="primary" />
                                                        </IconButton>
                                                    )}
                                                    {userData.linkedin && userData.linkedin !== 'pendding' && (
                                                        <IconButton href={userData.linkedin} target="_blank">
                                                            <LinkedIn color="primary" />
                                                        </IconButton>
                                                    )}
                                                </Box>
                                            </Box>
                                        </Grid>

                                        <Grid item xs={12} md={8}>
                                            <StyledTabs
                                                value={activeTab}
                                                onChange={handleTabChange}
                                                variant={isMobile ? 'scrollable' : 'standard'}
                                                scrollButtons="auto"
                                            >
                                                <StyledTab label="Overview" value="overview" icon={<Person />} />
                                                <StyledTab label="Edit Profile" value="editProfile" icon={<Edit />} />
                                                <StyledTab label="Upload Photo" value="uploadPhoto" icon={<PhotoCamera />} />
                                                <StyledTab label="Change Password" value="changePassword" icon={<Lock />} />
                                            </StyledTabs>

                                            <Divider sx={{ my: 2 }} />

                                            <Box sx={{ pt: 2 }}>
                                                {activeTab === 'overview' && renderProfileOverview()}
                                                {activeTab === 'editProfile' && renderEditProfile()}
                                                {activeTab === 'uploadPhoto' && renderImageUpload()}
                                                {activeTab === 'changePassword' && renderChangePassword()}
                                            </Box>
                                        </Grid>
                                    </Grid>
                                </CardContent>
                            </Card>
                        </Paper>
                    </motion.div>
                ) : (
                    <GuestUser />
                )}
            </Container>
            
            {/* Delete Account Modal */}
            <Modal
                open={showDeleteModal}
                onClose={() => setShowDeleteModal(false)}
                aria-labelledby="delete-account-modal"
            >
                <Box
                    sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        width: isMobile ? '90%' : 600,
                        bgcolor: 'background.paper',
                        boxShadow: 24,
                        p: 4,
                        borderRadius: 2
                    }}
                >
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                        <Typography variant="h6" id="delete-account-modal">
                            Delete Account Confirmation
                        </Typography>
                        <IconButton onClick={() => setShowDeleteModal(false)}>
                            <Close />
                        </IconButton>
                    </Box>
                    
                    <Alert severity="error" sx={{ mb: 3 }}>
                        Are you sure you want to delete your account? This action cannot be undone.
                    </Alert>
                    
                    <Typography variant="body1" paragraph>
                        <strong>Researcher:</strong> {userData.name}
                    </Typography>
                    
                    <Typography variant="body1" color="error" paragraph>
                        <strong>Warning:</strong> The following data will be permanently deleted:
                    </Typography>
                    
                    <ul>
                        <li><Typography variant="body1">All research viewed by you</Typography></li>
                        <li><Typography variant="body1">All research uploaded by you</Typography></li>
                        <li><Typography variant="body1">All research projects uploaded by you</Typography></li>
                        <li><Typography variant="body1">Your profile account information</Typography></li>
                    </ul>
                    
                    <Box display="flex" justifyContent="flex-end" mt={3}>
                        <Button
                            variant="outlined"
                            onClick={() => setShowDeleteModal(false)}
                            sx={{ mr: 2 }}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="contained"
                            color="error"
                            startIcon={<Delete />}
                            onClick={() => handleSubmit("DELETE")}
                        >
                            Delete My Account
                        </Button>
                    </Box>
                </Box>
            </Modal>
        </div>
    );
};

export default Profile;